import { Range, Button, Flex, Input, Label, Span } from '@fareye/ui';
import {
  ddMMMMYYYY,
  hhmmADoMMMYYYY,
  FaDownload,
  startCase,
  yyyyMmDd,
  HHmm,
  get,
  cancelToken,
} from '@fareye/utils';
import { parseVoltage, dateFormatter } from '@fareye/common';
import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
  TooltipProps,
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

interface SensorDataPoint {
  datetime: string;
  dateObj: Date;
  weight: number;
}

interface Range {
  start: Date | null;
  end: Date | null;
}

interface IHistoryListing {
  data: any;
  dataLength: number;
  onSelect: (id: any) => void;
  id: { [key: string]: any };
  historySlider: any;
  downloadCsv: any;
  currHistorySlider: any;
  setCurrHistorySlider: any;
  config?: any;
}

export const formatters: any = {
  startCase,
  dateFormatterTZ: dateFormatter
};

export const HEADER_INFLECTION = {
  headers: { 'Key-Inflection': 'camel', 'X-Change-Case': true }
}

export const LoadsensorListing = ({
  id,
  data,
  dataLength,
  onSelect,
  config = {}
}: IHistoryListing) => { config || {};

  const [fullData, setFullData] = useState<SensorDataPoint[]>([]);
  const [filteredData, setFilteredData] = useState<SensorDataPoint[]>([]);
  const [range, setRange] = useState<Range>({ start: null, end: null });
  const [tempStart, setTempStart] = useState<Date | null>(null);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const source = cancelToken().source()

  const getISTDateTime = () => {
    const now = new Date();
    const dateOptions: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    };
    const timeOptions: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };

    const endDate = new Intl.DateTimeFormat("en-CA", dateOptions).format(now); // Format: YYYY-MM-DD
    const end_time = new Intl.DateTimeFormat("en-GB", timeOptions).format(now); // Format: HH:mm

    return { endDate, end_time };
  };

  const firstDateTime =  data?.startDate

  let firstData = {
    startDate: yyyyMmDd(firstDateTime),
    start_time: HHmm(firstDateTime),
    vehicle_no: data?.vehicleNo
  };

  let endData: { endDate: string; end_time: string };

  if (data?.closedDate) {
    const endDateTime = data?.closedDate;
    endData = {
      endDate: yyyyMmDd(endDateTime),
      end_time: HHmm(endDateTime),
    };
  } else {
    const { endDate, end_time } = getISTDateTime();
    endData = {
      endDate,
      end_time,
    };
  }
  
  useEffect(() => {
    const fetchData = async () => {
      const url = `/api/v2/reports/way_point`
      const params = {
        endDate: endData["endDate"],
        end_time: endData["end_time"],
        interval: 1,
        startDate: firstData["startDate"],
        start_time: firstData["start_time"],
        'vehicleNo[]': firstData["vehicle_no"],
        pageNo: 1
      };

      const stringParams = Object.fromEntries(
        Object.entries(params).map(([key, value]) => [key, String(value)])
      );

      const queryParams = new URLSearchParams(stringParams).toString();
      const fullUrl = `${url}?${queryParams}`;


      const { data }  = await get(
        fullUrl,
        source.token,
        HEADER_INFLECTION
      )
      const dataArray = data.content ?? [];
      const data_list = dataArray.map((item: any) => {
          const dateObj = new Date(item.istTimestamp);
          return {
            datetime: hhmmADoMMMYYYY(item.istTimestamp),
            dateObj,
            weight: item.extraData?.weight ?? 0,
            };
          });
     setFullData(data_list);
     setFilteredData(data_list);
    }
    fetchData();
  }, []);

  const handleClick = (e: any) => {
    if (!e?.activePayload?.[0]) return;
    const point = e.activePayload[0].payload.dateObj;

    if (!tempStart) {
      setTempStart(point);
      setHoverDate(null);
    } else {
      const start = tempStart < point ? tempStart : point;
      const end = tempStart > point ? tempStart : point;

      const filtered = fullData.filter(
        (item) => item.dateObj >= start && item.dateObj <= end
      );

      setRange({ start, end });
      setFilteredData(filtered);
      setTempStart(null);
      setHoverDate(null);
    }
  };

  const handleMouseMove = (e: any) => {
    if (!e?.activePayload?.[0]) return;
    const point = e.activePayload[0].payload.dateObj;
    if (tempStart) {
      setHoverDate(point);
    }
  };

  const handleReset = () => {
    setRange({ start: null, end: null });
    setFilteredData(fullData);
    setTempStart(null);
    setHoverDate(null);
  };

  const refAreaStart = tempStart && hoverDate ? (tempStart < hoverDate ? hhmmADoMMMYYYY(tempStart) : hhmmADoMMMYYYY(hoverDate)) : null;
  const refAreaEnd = tempStart && hoverDate ? (tempStart > hoverDate ? hhmmADoMMMYYYY(tempStart) : hhmmADoMMMYYYY(hoverDate)) : null;

  return (
    <div style={{ width: '100%', height: 600, marginTop: 40 }}>
      <Button onClick={handleReset}
          style={{
            backgroundColor: '#f44336',
            color: '#fff',
            padding: '6px 12px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            Flex:'right',
            marginLeft:'70px'
          }}
        >Reset</Button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ marginLeft: 70 }}>
          Load Sensor Weight Chart{' '}
          {range.start && range.end && (
            <span style={{ color: 'green' }}>
              (Filtered from {hhmmADoMMMYYYY(range.start)} to {hhmmADoMMMYYYY(range.end)})
            </span>
          )}
        </p>
        
      </div>
      {filteredData.length > 0 ? (
        <ResponsiveContainer>
          <LineChart
            data={filteredData}
            margin={{ top: 20, right: 30, left: 70, bottom: 130 }}
            onClick={handleClick}
            onMouseMove={handleMouseMove}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="datetime"
              angle={-45}
              textAnchor="end"
              dy={5}
              minTickGap={10}
            />
            <YAxis tickCount={10}/>
            <Tooltip />
            <Line type="monotone" dataKey="weight" stroke="#8884d8" dot={false} />
            {refAreaStart && refAreaEnd && (
              <ReferenceArea
                x1={refAreaStart}
                x2={refAreaEnd}
                strokeOpacity={0.3}
                fill="#d0e7ff"
                fillOpacity={0.6}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <p>Loading chart...</p>
      )}
    </div>
  );
};
